import { DOMParser, DOMSerializer, type Node as PMNode } from 'prosemirror-model';

import { schema } from './schema';

function stripSuggestionMarksFromDOM(root: HTMLElement): void {
  // Unwrap <span class="suggestion-add"> / <span data-suggestion-add="true"> etc.
  const selectors = [
    'span[data-suggestion-add]',
    'span[data-suggestion-delete]',
    'span.suggestion-add',
    'span.suggestion-delete',
    'ins.inserted',
    'del.deleted',
  ];
  for (const selector of selectors) {
    root.querySelectorAll(selector).forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });
  }
}

function stripSuggestionMarksFromHTML(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  stripSuggestionMarksFromDOM(div);
  return div.innerHTML;
}

function unwrapEditorHTML(raw: string): string {
  // Strip <html> wrapper sometimes saved by legacy editors. Browsers parse
  // <html> inside an existing document weirdly, splintering text nodes.
  let html = raw.trim();
  html = html.replace(/^<html[^>]*>/i, '').replace(/<\/html>\s*$/i, '');
  // Unwrap a top-level <div class="report"> if present (CKE legacy).
  const reportMatch = html.match(/^\s*<div[^>]*class="[^"]*\breport\b[^"]*"[^>]*>([\s\S]*)<\/div>\s*$/i);
  if (reportMatch) html = reportMatch[1];
  // Unwrap the legacy direction wrapper:
  //   <div data-clipr-text-direction="…" dir="…" style="…">…</div>
  // The dir is preserved on inner blocks via getPersistedTextDirection +
  // setTextDirection on mount, so we only need to strip the outer chrome.
  const dirMatch = html.match(/^\s*<div[^>]*data-clipr-text-direction="[^"]*"[^>]*>([\s\S]*)<\/div>\s*$/i);
  if (dirMatch) html = dirMatch[1];
  return html.trim();
}

export function htmlToDoc(html: string | null | undefined): PMNode {
  const cleaned = typeof html === 'string' ? unwrapEditorHTML(html) : '';
  const div = document.createElement('div');
  div.innerHTML = cleaned;
  stripSuggestionMarksFromDOM(div);
  if (!div.firstChild) {
    div.appendChild(document.createElement('p'));
  }
  return DOMParser.fromSchema(schema).parse(div);
}

export function docToHTML(doc: PMNode, options: { stripSuggestions?: boolean } = {}): string {
  const fragment = DOMSerializer.fromSchema(schema).serializeFragment(doc.content);
  const div = document.createElement('div');
  div.appendChild(fragment);
  const html = div.innerHTML;
  return options.stripSuggestions ? stripSuggestionMarksFromHTML(html) : html;
}
